import React from 'react';
import { Eye } from 'lucide-react';
import FundingClientStatusBadge from '../FundingClientStatusBadge';
import { formatCurrency, formatDate } from '../../../utils/fundingWorkspaceFormatters';

export const formatClientCode = (client, index = 0) => {
  if (client?.clientCode) return client.clientCode;
  const suffix = client?.id
    ? String(client.id).replace(/\D/g, '').slice(-6).padStart(6, '0')
    : String(index + 1).padStart(6, '0');
  return `FG-CL-${suffix}`;
};

const FundingClientRosterTable = ({
  clients = [],
  rosterStats = {},
  onSelectClient,
  onApplicationInfo,
  onViewClient,
}) => (
  <div className="overflow-x-auto rounded-2xl border border-[var(--fg-line)]">
    <table className="min-w-full text-left text-sm" data-testid="funding-clients-table">
      <thead>
        <tr className="border-b border-[var(--fg-line)] bg-[var(--fg-panel-soft)] text-[var(--fg-muted)]">
          <th className="px-3 py-2 font-medium">
            <input type="checkbox" aria-label="Select all clients" onChange={() => {}} />
          </th>
          <th className="px-3 py-2 font-medium">Client ID</th>
          <th className="px-3 py-2 font-medium">Client name</th>
          <th className="px-3 py-2 font-medium">Business name</th>
          <th className="px-3 py-2 font-medium">Email</th>
          <th className="px-3 py-2 font-medium">Phone</th>
          <th className="px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2 font-medium">Readiness</th>
          <th className="px-3 py-2 font-medium">Last application</th>
          <th className="px-3 py-2 font-medium">Total approvals</th>
          <th className="px-3 py-2 font-medium">Total denied</th>
          <th className="px-3 py-2 font-medium">Type</th>
          <th className="px-3 py-2 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((client, index) => {
          const stats = rosterStats[client.id] || {};
          return (
            <tr
              key={client.id}
              data-testid={`funding-client-row-${client.id}`}
              className="cursor-pointer border-b border-[var(--fg-line)]/70 transition hover:bg-[var(--fg-panel-soft)]"
              onClick={() => onSelectClient?.(client)}
            >
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" aria-label={`Select ${client.name}`} />
              </td>
              <td className="px-3 py-3 font-mono text-xs font-semibold text-[var(--fg-accent)]">
                {formatClientCode(client, index)}
              </td>
              <td className="px-3 py-3 font-medium text-[var(--fg-text)]">{client.name}</td>
              <td className="px-3 py-3 text-[var(--fg-muted)]">{client.businessName || '—'}</td>
              <td className="px-3 py-3 text-[var(--fg-muted)]">{client.email || '—'}</td>
              <td className="px-3 py-3 text-[var(--fg-muted)]">{client.phone || '—'}</td>
              <td className="px-3 py-3">
                <FundingClientStatusBadge status={client.status} />
              </td>
              <td className="px-3 py-3 font-medium text-[var(--fg-text)]">
                {client.readinessScore != null ? `${client.readinessScore}%` : '—'}
              </td>
              <td className="px-3 py-3 text-[var(--fg-muted)]">
                {stats.lastApplicationDate ? formatDate(stats.lastApplicationDate) : '—'}
              </td>
              <td className="px-3 py-3 font-medium text-[var(--fg-text)]">
                {stats.totalApprovalAmount != null ? formatCurrency(stats.totalApprovalAmount) : '—'}
              </td>
              <td className="px-3 py-3 text-[var(--fg-text)]">
                {stats.totalDenied != null ? stats.totalDenied : '—'}
              </td>
              <td className="px-3 py-3">
                <span className="fg-meta-pill fg-meta-pill-sample">{client.clientType || (client.isSample ? 'Sample' : 'Live')}</span>
              </td>
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    data-testid={`application-info-${client.id}`}
                    onClick={() => {
                      onSelectClient?.(client);
                      onApplicationInfo?.(client);
                    }}
                    className="rounded-lg bg-[var(--fg-accent-soft)] px-2.5 py-1.5 text-xs font-semibold text-[var(--fg-accent)] hover:bg-[var(--fg-accent)] hover:text-white"
                  >
                    Application Info
                  </button>
                  <button
                    type="button"
                    onClick={() => onViewClient?.(client)}
                    className="rounded-lg border border-[var(--fg-line)] px-2.5 py-1.5 text-xs font-medium text-[var(--fg-text)] hover:bg-white"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Eye className="size-3.5" />
                      View
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default FundingClientRosterTable;
